/**
 * Classical German noun-ending heuristics.
 *
 * Mirrors backend/app/data/__init__.py::SUFFIX_RULES. Each rule predicts both
 * the genus (Genus) and the plural pattern from the suffix alone. Reliability
 * reflects empirical purity against the top-2k-frequency noun corpus:
 *   - strong: ≥95% gender-pure AND ≥90% plural-pure (underline solid)
 *   - medium: notable exceptions exist (underline dashed)
 *
 * Plural encoding:
 *   ""       plural identical to singular
 *   "-en"    append -en  (die Zeitung → Zeitungen)
 *   "-n"     append -n   (die Familie → Familien)
 *   "-e"     append -e   (der Frühling → Frühlinge)
 *   "-se"    append -se  (das Ergebnis → Ergebnisse)
 *   "→-en"   replace suffix with -en (das Museum → Museen, der Sozialismus → Sozialismen)
 *   "¨-er"   umlaut + -er (der Irrtum → Irrtümer)
 */

const GENDER_PATTERNS_RAW = [
  // Feminine — strong
  { suffix: 'igkeit', gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'schaft', gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'ität',   gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'tion',   gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'sion',   gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'heit',   gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'keit',   gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'ung',    gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'tät',    gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'enz',    gender: 'feminine',  plural: '-en', reliability: 'strong' },
  { suffix: 'age',    gender: 'feminine',  plural: '-n',  reliability: 'strong' },
  { suffix: 'ie',     gender: 'feminine',  plural: '-n',  reliability: 'strong' },
  // Feminine — medium
  { suffix: 'anz',    gender: 'feminine',  plural: '-en', reliability: 'medium' },
  { suffix: 'ät',     gender: 'feminine',  plural: '-en', reliability: 'medium' },
  { suffix: 'ik',     gender: 'feminine',  plural: '-en', reliability: 'medium' },
  { suffix: 'ei',     gender: 'feminine',  plural: '-en', reliability: 'medium' },
  { suffix: 'ur',     gender: 'feminine',  plural: '-en', reliability: 'medium' },

  // Masculine — strong
  { suffix: 'ismus',  gender: 'masculine', plural: '→-en', reliability: 'strong' },
  { suffix: 'ling',   gender: 'masculine', plural: '-e',  reliability: 'strong' },
  // Masculine — medium
  { suffix: 'ent',    gender: 'masculine', plural: '-en', reliability: 'medium' },
  { suffix: 'ant',    gender: 'masculine', plural: '-en', reliability: 'medium' },
  { suffix: 'ist',    gender: 'masculine', plural: '-en', reliability: 'medium' },
  { suffix: 'or',     gender: 'masculine', plural: '-en', reliability: 'medium' },
  { suffix: 'er',     gender: 'masculine', plural: '',    reliability: 'medium' },

  // Neuter — strong
  { suffix: 'chen',   gender: 'neuter',    plural: '',    reliability: 'strong' },
  { suffix: 'lein',   gender: 'neuter',    plural: '',    reliability: 'strong' },
  // Neuter — medium
  { suffix: 'ment',   gender: 'neuter',    plural: '-e',  reliability: 'medium' },
  { suffix: 'nis',    gender: 'neuter',    plural: '-se', reliability: 'medium' },
  { suffix: 'tum',    gender: 'neuter',    plural: '¨-er',reliability: 'medium' },
  { suffix: 'um',     gender: 'neuter',    plural: '→-en',reliability: 'medium' },
  { suffix: 'ma',     gender: 'neuter',    plural: '→-en',reliability: 'medium' },
];

// Longest-first so -igkeit wins over -keit, -tion over -ion, etc.
const GENDER_PATTERNS = [...GENDER_PATTERNS_RAW].sort(
  (a, b) => b.suffix.length - a.suffix.length
);

const PLURAL_SUFFIXES_ORDERED = ['en', 'er', 's', 'n', 'e'];

const UMLAUT_MAP = {
  'ä': 'a', 'ö': 'o', 'ü': 'u',
  'Ä': 'A', 'Ö': 'O', 'Ü': 'U',
};

function stripUmlauts(s) {
  return s.replace(/[äöüÄÖÜ]/g, (c) => UMLAUT_MAP[c] || c);
}

export function normalizeGender(value) {
  if (!value) return null;
  const lower = String(value).trim().toLowerCase();
  if (!lower) return null;
  if (['m', 'masc', 'mask', 'maskulin', 'masculine', 'der'].includes(lower)) {
    return 'masculine';
  }
  if (['f', 'fem', 'feminin', 'feminine', 'die'].includes(lower)) {
    return 'feminine';
  }
  if (['n', 'neut', 'neutr', 'neutrum', 'neuter', 'das'].includes(lower)) {
    return 'neuter';
  }
  return null;
}

/**
 * Detect a classical gender-indicating suffix on `word`.
 *
 * When `knownGender` is supplied, the match is only returned if the pattern's
 * predicted gender agrees with it (so we never teach a misleading rule on an
 * exception like `das Labor`). Without `knownGender`, returns the longest
 * matching rule regardless.
 *
 * Returns `{ suffix, gender, plural, reliability }` where `suffix` is the
 * substring sliced from the original (case-preserved) word, or null.
 */
export function detectGenderEnding(word, knownGender) {
  if (typeof word !== 'string') return null;
  const w = word.trim();
  if (!w) return null;
  const lower = w.toLowerCase();
  const normalized = normalizeGender(knownGender);

  for (const rule of GENDER_PATTERNS) {
    if (lower.length <= rule.suffix.length) continue;
    if (!lower.endsWith(rule.suffix)) continue;
    if (normalized && normalized !== rule.gender) continue;
    return {
      suffix: w.slice(-rule.suffix.length),
      gender: rule.gender,
      plural: rule.plural,
      reliability: rule.reliability,
    };
  }
  return null;
}

/**
 * Detect the plural suffix added to `singular` to form `plural`, ignoring
 * umlaut shifts. Returns the suffix substring from the plural form, or null
 * when no classical suffix pattern matches (e.g. umlaut-only plurals).
 */
export function detectPluralEnding(singular, plural) {
  if (typeof singular !== 'string' || typeof plural !== 'string') return null;
  const s = singular.trim();
  const p = plural.trim();
  if (!s || !p || s === p) return null;

  const ordered = [...PLURAL_SUFFIXES_ORDERED].sort((a, b) => b.length - a.length);
  const sNorm = stripUmlauts(s).toLowerCase();

  for (const suf of ordered) {
    if (p.length <= suf.length) continue;
    if (!p.toLowerCase().endsWith(suf)) continue;
    const stem = p.slice(0, -suf.length);
    if (stripUmlauts(stem).toLowerCase() === sNorm) {
      return p.slice(-suf.length);
    }
  }
  return null;
}

const ARTICLE_BY_GENDER = {
  masculine: 'der',
  feminine: 'die',
  neuter: 'das',
};

const PLURAL_PATTERN_LABEL = {
  '': 'unverändert',
  '-en': '+ -en',
  '-n': '+ -n',
  '-e': '+ -e',
  '-se': '+ -se',
  '→-en': 'Endung → -en',
  '¨-er': 'Umlaut + -er',
};

/**
 * Human-readable label for a plural pattern string from GENDER_PATTERNS.
 */
export function describePluralPattern(plural) {
  if (plural == null) return null;
  return PLURAL_PATTERN_LABEL[plural] ?? plural;
}

export function articleForGender(gender) {
  return ARTICLE_BY_GENDER[gender] || null;
}
